import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Flex,
  Form,
  Input,
  Mentions,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  notification,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, StarOutlined } from "@ant-design/icons";
import axiosInstance from "../axios";

const { Paragraph, Text } = Typography;

const DEFAULT_TITLE_TEMPLATE =
  "Home/Online Tutor Needed for Class {Class} - {Area}, {City}";
const DEFAULT_DESCRIPTION_TEMPLATE =
  "A parent in {Area}, {City} is looking for a qualified home tutor to teach Class {Class} {Subjects} following the {Board} curriculum. The tutor will conduct one-to-one home tuition at the student's residence in {Area}. The ideal tutor should have strong subject knowledge, excellent communication skills, and the ability to create an engaging learning environment that helps the student improve academically.";

const FALLBACK_VARIABLES = [
  { key: "Class", description: "Student class / study level" },
  { key: "Area", description: "Locality / area from student address" },
  { key: "City", description: "City name" },
  { key: "Subjects", description: "Required subjects" },
  { key: "Board", description: "Board / stream name" },
  { key: "Mode", description: "Home, Online, or Home/Online" },
  { key: "Salary", description: "Fee display e.g. ₹8000/month" },
  { key: "DaysPerWeek", description: "Days per week" },
  { key: "TuitionHours", description: "Hours per day" },
  { key: "GenderPreference", description: "Tutor gender preference" },
  { key: "StartDate", description: "Preferred start date" },
];

const JobTemplates = () => {
  const [data, setData] = useState([]);
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });

  const variableList = variables.length > 0 ? variables : FALLBACK_VARIABLES;

  const mentionOptions = useMemo(
    () =>
      variableList.map((item) => ({
        key: item.key,
        // Mentions inserts prefix + value, so value ends with "}" → "{Class}"
        value: `${item.key}}`,
        label: (
          <div>
            <Text strong>{`{${item.key}}`}</Text>
            <div style={{ fontSize: 12, color: "#666" }}>{item.description}</div>
          </div>
        ),
      })),
    [variableList],
  );

  const fetchData = async (page, limit) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/v1/cre/job-templates", {
        params: { page, limit },
      });
      setData(response.data?.data || []);
      setVariables(response.data?.meta?.variables || []);
      setPagination({
        current: page,
        pageSize: limit,
        total: response.data?.meta?.total || 0,
      });
    } catch (error) {
      notification.error({
        message: "Failed to load templates",
        description: error.response?.data?.message || "Could not fetch job templates.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(pagination.current, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldsValue({
      name: "",
      title_template: DEFAULT_TITLE_TEMPLATE,
      description_template: DEFAULT_DESCRIPTION_TEMPLATE,
      is_default: false,
      is_active: true,
    });
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingItem(record);
    form.setFieldsValue({
      name: record.name,
      title_template: record.title_template,
      description_template: record.description_template,
      is_default: Boolean(record.is_default),
      is_active: Boolean(record.is_active),
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitLoading(true);

      if (editingItem) {
        const response = await axiosInstance.put(`/v1/cre/job-templates/${editingItem.id}`, values);
        if (response.data?.success) {
          notification.success({ message: response.data.message || "Template updated" });
        } else {
          notification.error({
            message: "Update failed",
            description: response.data?.message || "Unable to update template",
          });
          return;
        }
      } else {
        const response = await axiosInstance.post("/v1/cre/job-templates", values);
        if (response.data?.success) {
          notification.success({ message: response.data.message || "Template created" });
        } else {
          notification.error({
            message: "Create failed",
            description: response.data?.message || "Unable to create template",
          });
          return;
        }
      }

      setModalOpen(false);
      fetchData(pagination.current, pagination.pageSize);
    } catch (error) {
      if (error.errorFields) return;
      notification.error({
        message: "Save failed",
        description: error.response?.data?.message || "Unable to save template",
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSetDefault = async (id) => {
    try {
      const response = await axiosInstance.post(`/v1/cre/job-templates/${id}/set-default`);
      if (response.data?.success) {
        notification.success({ message: response.data.message || "Default template updated" });
        fetchData(pagination.current, pagination.pageSize);
      } else {
        notification.error({
          message: "Update failed",
          description: response.data?.message || "Unable to set default template",
        });
      }
    } catch (error) {
      notification.error({
        message: "Update failed",
        description: error.response?.data?.message || "Unable to set default template",
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await axiosInstance.delete(`/v1/cre/job-templates/${id}`);
      if (response.data?.success) {
        notification.success({ message: "Template deleted successfully" });
        fetchData(pagination.current, pagination.pageSize);
      } else {
        notification.error({
          message: "Delete failed",
          description: response.data?.message || "Unable to delete template",
        });
      }
    } catch (error) {
      notification.error({
        message: "Delete failed",
        description: error.response?.data?.message || "Unable to delete template",
      });
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <Space>
          <span>{name}</span>
          {record.is_default ? <Tag color="blue">Default</Tag> : null}
        </Space>
      ),
    },
    {
      title: "Title template",
      dataIndex: "title_template",
      key: "title_template",
      ellipsis: true,
    },
    {
      title: "Active",
      dataIndex: "is_active",
      key: "is_active",
      width: 90,
      render: (active) => (active ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>),
    },
    {
      title: "Action",
      key: "action",
      width: 260,
      render: (_, record) => (
        <Space>
          <Button type="primary" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            Edit
          </Button>
          {!record.is_default && (
            <Button size="small" icon={<StarOutlined />} onClick={() => handleSetDefault(record.id)}>
              Set default
            </Button>
          )}
          {!record.is_default && (
            <Popconfirm
              title="Delete this template?"
              onConfirm={() => handleDelete(record.id)}
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button danger size="small" icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Flex vertical gap={12} style={{ overflow: "scroll", marginLeft: "5px", width: "100%" }}>
      <Flex justify="space-between" align="flex-start" gap={16} wrap="wrap">
        <div>
          <Text strong style={{ fontSize: 16 }}>
            Job Templates
          </Text>
          <Paragraph type="secondary" style={{ marginBottom: 0, maxWidth: 720 }}>
            Type <Text code>{"{"}</Text> in title or description to insert variables like{" "}
            {"{Class}"}, {"{Area}"}, {"{City}"}. The default template is used when publishing a deal
            to the jobs portal.
          </Paragraph>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Add Template
        </Button>
      </Flex>

      {variableList.length > 0 && (
        <Flex gap={8} wrap="wrap">
          {variableList.map((item) => (
            <Tag key={item.key} title={item.description}>
              {`{${item.key}}`}
            </Tag>
          ))}
        </Flex>
      )}

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: [10, 25, 50],
        }}
        onChange={(next) => fetchData(next.current, next.pageSize)}
        scroll={{ x: 1000 }}
      />

      <Modal
        title={editingItem ? "Edit Job Template" : "Add Job Template"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        confirmLoading={submitLoading}
        okText={editingItem ? "Update" : "Create"}
        width={800}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Template name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input placeholder="e.g. Default Home/Online Tutor" />
          </Form.Item>

          <Form.Item
            name="title_template"
            label="Title template"
            rules={[{ required: true, message: "Title template is required" }]}
            extra='Type "{" to see variable suggestions. Example: Home/Online Tutor Needed for Class {Class} - {Area}, {City}'
          >
            <Mentions
              prefix="{"
              rows={2}
              placeholder='Type "{" for variables like Class, Area, City'
              options={mentionOptions}
              filterOption={(input, option) =>
                String(option?.value || "")
                  .toLowerCase()
                  .includes(String(input || "").toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            name="description_template"
            label="Description template"
            rules={[{ required: true, message: "Description template is required" }]}
            extra='Type "{" to insert variables'
          >
            <Mentions
              prefix="{"
              rows={7}
              placeholder='Type "{" for variables like Class, Area, City, Subjects, Board'
              options={mentionOptions}
              filterOption={(input, option) =>
                String(option?.value || "")
                  .toLowerCase()
                  .includes(String(input || "").toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item name="is_default" label="Default template" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Flex>
  );
};

export default JobTemplates;
